import { Car, Settings, History, Calendar, FileCheck, Wrench, PaintBucket, Receipt, FileText } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const DashboardSidebar = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast({
        title: "Succesvol uitgelogd",
        description: "Je bent nu uitgelogd.",
      });
      navigate('/login');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Onbekende fout bij uitloggen';
      toast({
        variant: "destructive",
        title: "Uitloggen mislukt",
        description: errorMessage,
      });
    }
  };

  const menuItems = [
    {
      title: "Wagenpark",
      icon: Car,
      submenuItems: [
        { title: "Voertuig toevoegen", icon: Car, href: "/app/add-vehicle" },
        { title: "Voertuigen beheren", icon: Settings, href: "/app/manage-vehicles" },
      ]
    },
    {
      title: "Onderhoud",
      icon: Wrench,
      submenuItems: [
        { title: "Afspraak maken", icon: Calendar, href: id ? `/app/vehicles/${id}/maintenance-history` : "#" },
        { title: "Onderhoud historie", icon: History, href: id ? `/app/vehicles/${id}/maintenance-overview` : "#" },
      ]
    },
    {
      title: "Customizing",
      icon: PaintBucket,
      submenuItems: [
        { title: "Chiptuning", icon: Settings, href: "#" },
        { title: "Wrappen", icon: PaintBucket, href: "#" },
        { title: "Paint Protection Film", icon: PaintBucket, href: "#" },
        { title: "Custom Stickers", icon: FileText, href: "#" },
        { title: "Mods", icon: Settings, href: "#" },
      ]
    },
    {
      title: "Financieel",
      icon: Receipt,
      submenuItems: [
        { title: "Facturen", icon: Receipt, href: "#" },
        { title: "Offertes", icon: FileText, href: "#" },
      ]
    },
  ];

  const defaultExpandedItems = menuItems.map(item => item.title);

  return (
    <Sidebar className="bg-black bg-gradient-to-r from-[#B1A06D]/10 to-[#B1A06D]/5 border-r border-white/10">
      <SidebarContent className="pb-4">
        <div className="flex h-[60px] items-center border-b border-white/10 px-6">
          <div className="flex items-center gap-2 font-semibold text-white">
            <a href="#" className="flex items-center">
              <img 
                src="/lovable-uploads/f0397fdb-65b1-425f-a824-0229bd3e12d4.png" 
                alt="Logo" 
                className="h-8 w-auto max-w-[200px]" 
              />
            </a>
          </div>
        </div>

        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <Accordion type="multiple" defaultValue={defaultExpandedItems} className="w-full">
                {menuItems.map((item) => (
                  <AccordionItem key={item.title} value={item.title} className="border-b-0">
                    <AccordionTrigger className="px-4 py-2 text-white hover:no-underline">
                      <div className="flex items-center gap-2">
                        <item.icon className="h-4 w-4 text-[#B1A06D]" />
                        <span>{item.title}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-1 py-1">
                        {item.submenuItems?.map((subItem) => (
                          <SidebarMenuItem key={subItem.title}>
                            <SidebarMenuButton
                              asChild
                              className="px-6 py-1.5 text-white hover:bg-white/10"
                            >
                              <a href={subItem.href} className="flex items-center gap-2">
                                <subItem.icon className="h-4 w-4 text-[#B1A06D]" />
                                <span className="font-light text-white">{subItem.title}</span>
                              </a>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <div className="mt-auto">
          <div className="flex items-center gap-2 px-4 py-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10">
              <span className="text-xs font-medium text-white">SC</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-medium text-white">shadcn</span>
              <span className="text-xs text-white/60">m@example.com</span>
            </div>
          </div>
        </div>
      </SidebarContent>
    </Sidebar>
  );
};

export default DashboardSidebar;
